import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';

interface EmptyStateProps {
  icon?: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  primaryAction?: {
    label: string;
    onPress: () => void;
  };
  secondaryAction?: {
    label: string;
    onPress: () => void;
  };
  tips?: string[];
}

export function EmptyState({
  icon = 'albums-outline',
  title,
  description,
  primaryAction,
  secondaryAction,
  tips,
}: EmptyStateProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: theme.spacing.xxl,
          paddingVertical: theme.spacing.huge,
        },
        iconContainer: {
          width: 120,
          height: 120,
          borderRadius: 60,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: theme.spacing.xl,
        },
        title: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
          textAlign: 'center',
          marginBottom: theme.spacing.md,
        },
        description: {
          ...theme.typography.lg,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.xxl,
          maxWidth: 400,
        },
        actionButton: {
          minWidth: 200,
          marginBottom: theme.spacing.md,
        },
        tipsContainer: {
          marginTop: theme.spacing.xxxl,
          width: '100%',
          maxWidth: 400,
        },
        tipsDivider: {
          height: 1,
          backgroundColor: theme.borders.borderColor,
          marginBottom: theme.spacing.xl,
        },
        tipsTitle: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.lg,
        },
        tipItem: {
          flexDirection: 'row',
          marginBottom: theme.spacing.md,
          alignItems: 'flex-start',
        },
        tipBullet: {
          ...theme.typography.md,
          color: theme.colors.accent,
          marginRight: theme.spacing.sm,
          marginTop: 2,
        },
        tipText: {
          flex: 1,
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
      }),
    [theme],
  );

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons name={icon} size={80} color={theme.colors.textTertiary} />
      </View>

      <Text style={styles.title}>{title}</Text>
      <Text style={styles.description}>{description}</Text>

      {primaryAction && (
        <TouchableOpacity
          style={[commonStyles.primaryButton, styles.actionButton]}
          onPress={primaryAction.onPress}
        >
          <Ionicons name="sparkles-outline" size={20} color={theme.colors.backgroundPrimary} />
          <Text style={commonStyles.primaryButtonText}>{primaryAction.label}</Text>
        </TouchableOpacity>
      )}

      {secondaryAction && (
        <TouchableOpacity
          style={commonStyles.link}
          onPress={secondaryAction.onPress}
        >
          <Text style={commonStyles.linkText}>{secondaryAction.label}</Text>
        </TouchableOpacity>
      )}

      {tips && tips.length > 0 && (
        <View style={styles.tipsContainer}>
          <View style={styles.tipsDivider} />
          <Text style={styles.tipsTitle}>💡 Quick Tips</Text>
          {tips.map((tip, index) => (
            <View key={index} style={styles.tipItem}>
              <Text style={styles.tipBullet}>•</Text>
              <Text style={styles.tipText}>{tip}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
